/**
 * useParticipationAudioPipeline ✦ iter205
 * ─────────────────────────────────────────────────────────────
 * Capture-grade infrastructure for voice. Parallel to (but
 * deliberately separate from) `useBoothCameraPipeline` — audio has
 * different emotional and technical behaviour and the two pipelines
 * stay decoupled to prevent architectural contamination.
 *
 * Layers (per Craig, iter205):
 *
 *   1. CAPTURE
 *      • Profile-driven getUserMedia({audio}).
 *      • Device selection (mic input).
 *      • Sample rate / echo cancellation / noise suppression / AGC
 *        flags from the active profile.
 *
 *   2. WEBAUDIO GRAPH
 *      MediaStreamSource
 *         → BiquadFilter (highpass — low-cut at profile.lowCutHz)
 *         → DynamicsCompressor (clipping guard, gentle ratio)
 *         → GainNode (peak normalisation toward profile.peakTargetLinear)
 *         → AnalyserNode (live telemetry — peakLevel, presenceFloor, clipping)
 *         → MediaStreamDestination (recorder source)
 *      Intelligibility-first, NOT studio-clean. The room is part of
 *      the memory.
 *
 *   3. RECORDING
 *      • MediaRecorder on the processed stream.
 *      • Honest mime negotiation (reads rec.mimeType for the blob).
 *      • Safari mp4 fallback.
 *      • Returns BOTH `rawBlob` (untouched source) AND `processedBlob`
 *        (tail-trimmed, peak-normalised upload artifact). The raw
 *        blob is preserved for future recap / AI cleanup / archive
 *        mastering work.
 *
 *   4. EMOTIONAL CONTINUITY
 *      • Warm-start.
 *      • track.onended interruption recovery.
 *      • devicechange listener for Bluetooth / headphone transitions
 *        (AirPods reconnect, car Bluetooth, DJ speaker pairing, etc.).
 *      • Long-session resilience (visibilitychange / pagehide /
 *        beforeunload).
 *
 *   5. SILENCE TRIMMING
 *      • Tail-only. Head silence is PRESERVED (inhale, hesitation,
 *        room tone — the most human part of a voice memory).
 *      • RMS-based: trims trailing silence below profile.silenceTrimRMS
 *        down to profile.tailSilenceMaxMs of room tone.
 *      • Operates on a decoded copy of the recording — raw blob is
 *        never mutated.
 *
 *   6. TELEMETRY
 *      • peakLevel — current RMS (0..1)
 *      • presenceFloor — moving baseline RMS (the "room when quiet")
 *      • clipping — flagged frames above profile.clippingGuardLinear
 *      • rmsHistory — bounded ring buffer (last 240 frames ≈ 4s @ 60Hz)
 *      • silenceWindows + interruptionCount + averageLoudness for
 *        future emotional scoring (Event Film, recap sequencing,
 *        highlight selection, archive indexing).
 *
 * Usage:
 *
 *    const mic = useParticipationAudioPipeline({ profile: 'voice_message' });
 *    useEffect(() => { if (stage === 'capture') mic.start(); }, [stage]);
 *    const { rawBlob, processedBlob, mime } = await mic.stopRecording();
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getAudioProfile,
  audioProfileToConstraints,
} from '../lib/booth-os/participationAudioProfiles';
import { emit as soakEmit } from '../lib/booth-os/soakTelemetry';

const RMS_HISTORY_LEN = 240;     // ~4s at 60Hz
const PRESENCE_EMA_ALPHA = 0.04; // slow moving baseline

function negotiateAudioMime(candidates) {
  if (typeof window === 'undefined' || !window.MediaRecorder) return '';
  const list = Array.isArray(candidates) ? candidates : [];
  for (const cand of list) {
    try {
      if (window.MediaRecorder.isTypeSupported(cand)) return cand;
    } catch { /* some UAs throw on unknown candidates */ }
  }
  return '';
}

/**
 * Tail-only silence trim. Operates on a decoded AudioBuffer copy —
 * the raw blob upstream is never mutated. Returns an encoded WAV blob
 * (browser-universal) of the trimmed audio.
 *
 * Note: we intentionally re-encode as WAV here because re-encoding
 * to opus/aac client-side requires WASM codecs we don't ship. The
 * SERVER can transcode the trimmed WAV → opus for storage if size
 * matters; the raw original is preserved alongside for archive truth.
 */
async function trimTailSilence(rawBlob, profile) {
  try {
    if (typeof window === 'undefined' || !window.AudioContext) return rawBlob;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const arr = await rawBlob.arrayBuffer();
    const buffer = await ctx.decodeAudioData(arr.slice(0));
    const { numberOfChannels, sampleRate, length } = buffer;
    const tailMaxSamples = Math.floor((profile.tailSilenceMaxMs / 1000) * sampleRate);
    const window_ = Math.max(256, Math.floor(sampleRate * 0.02)); // 20ms window
    const threshold = profile.silenceTrimRMS;

    // Scan from end, find first window whose RMS rises above threshold.
    let lastVoiceSample = length;
    const ch0 = buffer.getChannelData(0);
    for (let i = length - window_; i >= 0; i -= window_) {
      let sumSq = 0;
      for (let j = 0; j < window_; j += 1) sumSq += ch0[i + j] * ch0[i + j];
      const rms = Math.sqrt(sumSq / window_);
      if (rms > threshold) { lastVoiceSample = i + window_; break; }
    }
    const targetLength = Math.min(length, lastVoiceSample + tailMaxSamples);
    if (targetLength >= length - window_) {
      ctx.close().catch(() => {});
      return rawBlob; // nothing to trim
    }

    // Encode the trimmed PCM as WAV (mono — voice memos don't benefit
    // from preserving stereo room mics through a single browser path).
    const trimmedChannels = [];
    for (let c = 0; c < numberOfChannels; c += 1) {
      trimmedChannels.push(buffer.getChannelData(c).slice(0, targetLength));
    }
    const wav = encodeWav(trimmedChannels, sampleRate);
    ctx.close().catch(() => {});
    return new Blob([wav], { type: 'audio/wav' });
  } catch {
    // ANY trim failure must return the raw blob — never lose a memory.
    return rawBlob;
  }
}

function encodeWav(channels, sampleRate) {
  const numCh = channels.length;
  const numSamples = channels[0].length;
  const bytesPerSample = 2;
  const blockAlign = numCh * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = numSamples * blockAlign;
  const buf = new ArrayBuffer(44 + dataSize);
  const v = new DataView(buf);
  let p = 0;
  const writeStr = (s) => { for (let i = 0; i < s.length; i += 1) v.setUint8(p + i, s.charCodeAt(i)); p += s.length; };
  writeStr('RIFF'); v.setUint32(p, 36 + dataSize, true); p += 4;
  writeStr('WAVE'); writeStr('fmt '); v.setUint32(p, 16, true); p += 4;
  v.setUint16(p, 1, true); p += 2;
  v.setUint16(p, numCh, true); p += 2;
  v.setUint32(p, sampleRate, true); p += 4;
  v.setUint32(p, byteRate, true); p += 4;
  v.setUint16(p, blockAlign, true); p += 2;
  v.setUint16(p, bytesPerSample * 8, true); p += 2;
  writeStr('data'); v.setUint32(p, dataSize, true); p += 4;
  for (let i = 0; i < numSamples; i += 1) {
    for (let c = 0; c < numCh; c += 1) {
      const s = Math.max(-1, Math.min(1, channels[c][i]));
      v.setInt16(p, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      p += 2;
    }
  }
  return buf;
}

export function useParticipationAudioPipeline({
  profile: profileName = 'voice_message',
  initialDeviceId,
  onError,
} = {}) {
  const profile = getAudioProfile(profileName);

  const rawStreamRef = useRef(null);
  const processedStreamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(0);
  const interruptionCountRef = useRef(0);
  const silenceWindowsRef = useRef([]);
  const presenceEmaRef = useRef(0);

  // iter206 — voicePresenceMoments. While recording, every contiguous
  // speaking window (RMS rising above silenceTrimRMS) is captured as
  // a moment `{ tStart, tEnd, meanRms, peakRms }`. Cheap to record,
  // huge future leverage for Event Film ✦ sequencing, recap highlight
  // selection, and "intelligently denoise" passes downstream.
  const voicePresenceMomentsRef = useRef([]);
  const currentMomentRef = useRef(null);
  const recordingStartTRef = useRef(0);
  // iter206 — rawRoomToneRMS. Sampled in the first ~500ms after
  // startRecording so we lock the "room when quiet" baseline before
  // anyone speaks. Preserved alongside the rawBlob so future cleanup
  // passes can intelligently denoise, normalise per venue, or classify
  // dancefloor vs quiet-room WITHOUT reprocessing the entire file.
  const rawRoomToneRMSRef = useRef(0);
  const rawRoomToneSamplesRef = useRef([]);
  const rawRoomToneSampledRef = useRef(false);

  const [status, setStatus] = useState('idle'); // idle | requesting | live | recording | denied | error
  const [error, setError] = useState(null);
  const [deviceId, setDeviceId] = useState(initialDeviceId || null);
  const [devices, setDevices] = useState([]);
  const [recordingMime, setRecordingMime] = useState('');

  // Live telemetry — these update at rAF frequency while the
  // pipeline is live or recording.
  const [peakLevel, setPeakLevel] = useState(0);
  const [presenceFloor, setPresenceFloor] = useState(0);
  const [clipping, setClipping] = useState(false);

  const reportError = useCallback((msg, err) => {
    setError(msg);
    if (typeof onError === 'function') { try { onError(msg, err); } catch { /* swallow */ } }
  }, [onError]);

  const stop = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    try { recorderRef.current?.stop?.(); } catch { /* idempotent */ }
    if (rawStreamRef.current) {
      try { rawStreamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* hardware races */ }
      rawStreamRef.current = null;
      soakEmit('mic:stop', { profile: profileName });
    }
    if (processedStreamRef.current) {
      try { processedStreamRef.current.getTracks().forEach((t) => t.stop()); } catch {}
      processedStreamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch {}
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    setStatus('idle');
    setPeakLevel(0);
    setClipping(false);
  }, [profileName]);

  const startTelemetry = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const an = analyserRef.current;
    if (!an) return;
    const data = new Float32Array(an.fftSize);
    let silenceRun = 0;
    const sampleRateMs = 1000 / 60;
    const tick = () => {
      if (!analyserRef.current) return;
      an.getFloatTimeDomainData(data);
      let sumSq = 0;
      let peak = 0;
      for (let i = 0; i < data.length; i += 1) {
        const s = data[i];
        sumSq += s * s;
        const a = Math.abs(s);
        if (a > peak) peak = a;
      }
      const rms = Math.sqrt(sumSq / data.length);
      setPeakLevel(rms);
      setClipping(peak > profile.clippingGuardLinear);
      // Presence floor: slow EMA over quiet windows only — gives us
      // "what the room feels like when nobody is speaking".
      if (rms < profile.silenceTrimRMS) {
        presenceEmaRef.current = (1 - PRESENCE_EMA_ALPHA) * presenceEmaRef.current
          + PRESENCE_EMA_ALPHA * rms;
        setPresenceFloor(presenceEmaRef.current);
        silenceRun += sampleRateMs;
        // iter206 — close any open speaking window when we drop back
        // into silence for ≥ 80ms. Sub-80ms gaps (between words) stay
        // inside the same moment to avoid fragmenting natural speech.
        if (silenceRun >= 80 && currentMomentRef.current && recordingStartTRef.current) {
          const m = currentMomentRef.current;
          m.tEnd = performance.now() - recordingStartTRef.current;
          m.meanRms = m._frames > 0 ? m._rmsSum / m._frames : 0;
          delete m._rmsSum; delete m._frames;
          voicePresenceMomentsRef.current.push(m);
          currentMomentRef.current = null;
        }
      } else {
        if (silenceRun > 200) silenceWindowsRef.current.push(silenceRun);
        silenceRun = 0;
        // iter206 — open or extend a speaking moment while recording.
        if (recordingStartTRef.current) {
          const tNow = performance.now() - recordingStartTRef.current;
          if (!currentMomentRef.current) {
            currentMomentRef.current = {
              tStart: tNow, tEnd: tNow, peakRms: rms, meanRms: rms,
              _rmsSum: rms, _frames: 1,
            };
          } else {
            const m = currentMomentRef.current;
            m.tEnd = tNow;
            if (rms > m.peakRms) m.peakRms = rms;
            m._rmsSum += rms; m._frames += 1;
          }
        }
      }
      // iter206 — sample raw room tone in the first ~500ms of
      // recording. Only counts quiet frames; the baseline is locked
      // when we have ≥6 valid samples or 500ms elapsed.
      if (recordingStartTRef.current && !rawRoomToneSampledRef.current) {
        const elapsed = performance.now() - recordingStartTRef.current;
        if (rms < profile.silenceTrimRMS) rawRoomToneSamplesRef.current.push(rms);
        if (elapsed >= 500 || rawRoomToneSamplesRef.current.length >= 12) {
          const arr = rawRoomToneSamplesRef.current;
          rawRoomToneRMSRef.current = arr.length
            ? arr.reduce((a, b) => a + b, 0) / arr.length
            : presenceEmaRef.current;
          rawRoomToneSampledRef.current = true;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [profile]);

  const start = useCallback(async (override = {}) => {
    stop();
    setStatus('requesting');
    setError(null);
    try {
      const constraints = audioProfileToConstraints(profile, {
        deviceId: override.deviceId || deviceId || undefined,
      });
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      rawStreamRef.current = stream;
      soakEmit('mic:start', { profile: profileName });
      // Build the WebAudio graph. Intelligibility-first — every node
      // is configured to PROTECT integrity rather than to maximise
      // loudness or strip room tone.
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx({ sampleRate: profile.sampleRate });
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const lowCut = ctx.createBiquadFilter();
      lowCut.type = 'highpass';
      lowCut.frequency.value = profile.lowCutHz;
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.setValueAtTime(profile.compressorThresholdDb, ctx.currentTime);
      comp.ratio.setValueAtTime(profile.compressorRatio, ctx.currentTime);
      comp.knee.setValueAtTime(8, ctx.currentTime);
      comp.attack.setValueAtTime(0.005, ctx.currentTime);
      comp.release.setValueAtTime(0.12, ctx.currentTime);
      const gain = ctx.createGain();
      gain.gain.value = profile.peakTargetLinear;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.6;
      const dest = ctx.createMediaStreamDestination();
      source.connect(lowCut).connect(comp).connect(gain).connect(analyser).connect(dest);
      analyserRef.current = analyser;
      processedStreamRef.current = dest.stream;

      // Stream-interruption recovery — OS suspends the track (lock
      // screen, headphone unplug etc.) — surface a recoverable error.
      stream.getAudioTracks().forEach((t) => {
        t.onended = () => {
          interruptionCountRef.current += 1;
          soakEmit('mic:track-ended', { profile: profileName });
          soakEmit('mic:interrupt', { profile: profileName });
          if (rawStreamRef.current === stream) {
            reportError('The microphone was paused by the device. Tap to bring it back.');
            setStatus('idle');
          }
        };
      });

      // Device list (mic switcher).
      try {
        const list = await navigator.mediaDevices.enumerateDevices();
        setDevices(list.filter((d) => d.kind === 'audioinput'));
      } catch { /* no permission to list — fine */ }

      setStatus('live');
      startTelemetry();
      return processedStreamRef.current;
    } catch (err) {
      const denied = err && (err.name === 'NotAllowedError' || err.name === 'SecurityError');
      setStatus(denied ? 'denied' : 'error');
      reportError(
        denied
          ? 'Microphone access is blocked. Allow the microphone, then try again.'
          : 'We couldn’t reach the microphone. Check the permissions and try once more.',
        err,
      );
      return null;
    }
  }, [profile, deviceId, reportError, stop, startTelemetry]);

  const switchDevice = useCallback((nextId) => {
    setDeviceId(nextId);
    return start({ deviceId: nextId });
  }, [start]);

  const startRecording = useCallback(() => {
    if (!processedStreamRef.current) return false;
    if (typeof window === 'undefined' || !window.MediaRecorder) {
      reportError('This browser can’t record on its own. Try the latest Chrome or Safari.');
      return false;
    }
    chunksRef.current = [];
    // iter206 — reset voicePresenceMoments + roomTone capture state
    // for THIS recording (the live telemetry refs persist across
    // sessions for the debug surface).
    voicePresenceMomentsRef.current = [];
    currentMomentRef.current = null;
    rawRoomToneSamplesRef.current = [];
    rawRoomToneSampledRef.current = false;
    rawRoomToneRMSRef.current = 0;
    recordingStartTRef.current = performance.now();
    const mime = negotiateAudioMime(profile.mimeCandidates);
    setRecordingMime(mime);
    try {
      const opts = { ...(mime ? { mimeType: mime } : {}) };
      if (profile.audioBitsPerSecond) opts.audioBitsPerSecond = profile.audioBitsPerSecond;
      const rec = new window.MediaRecorder(processedStreamRef.current, opts);
      recorderRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.start(250);
      setStatus('recording');
      soakEmit('mic:rec-start', { profile: profileName, mime });
      return true;
    } catch (err) {
      reportError('The recorder didn’t open. Try once more, slowly.', err);
      return false;
    }
  }, [profile, reportError]);

  const stopRecording = useCallback(() => new Promise((resolve) => {
    const rec = recorderRef.current;
    // iter206 — finalise any open speaking moment so the last
    // sentence is always represented.
    const finaliseMoments = () => {
      if (currentMomentRef.current && recordingStartTRef.current) {
        const m = currentMomentRef.current;
        m.tEnd = performance.now() - recordingStartTRef.current;
        m.meanRms = m._frames > 0 ? m._rmsSum / m._frames : 0;
        delete m._rmsSum; delete m._frames;
        voicePresenceMomentsRef.current.push(m);
        currentMomentRef.current = null;
      }
      recordingStartTRef.current = 0;
    };
    if (!rec) {
      finaliseMoments();
      return resolve({
        rawBlob: null, processedBlob: null, mime: '', ext: '',
        rawRoomToneRMS: rawRoomToneRMSRef.current,
        voicePresenceMoments: voicePresenceMomentsRef.current.slice(),
      });
    }
    if (rec.state === 'inactive') {
      const mime = rec.mimeType || recordingMime || 'audio/webm';
      const rawBlob = new Blob(chunksRef.current, { type: mime });
      finaliseMoments();
      return resolve({
        rawBlob, processedBlob: rawBlob, mime,
        ext: mime.includes('mp4') ? 'm4a' : 'webm',
        rawRoomToneRMS: rawRoomToneRMSRef.current,
        voicePresenceMoments: voicePresenceMomentsRef.current.slice(),
      });
    }
    rec.onstop = async () => {
      const mime = rec.mimeType || recordingMime || 'audio/webm';
      const rawBlob = new Blob(chunksRef.current, { type: mime });
      // Tail-only trim — head silence is preserved (inhale, hesitation,
      // room tone are the most human part of a voice memory).
      const processedBlob = await trimTailSilence(rawBlob, profile);
      finaliseMoments();
      setStatus((s) => (s === 'recording' ? 'live' : s));
      soakEmit('mic:rec-stop', { profile: profileName, mime, bytes: rawBlob.size });
      resolve({
        rawBlob,
        processedBlob,
        mime,
        ext: mime.includes('mp4') ? 'm4a' : 'webm',
        rawRoomToneRMS: rawRoomToneRMSRef.current,
        voicePresenceMoments: voicePresenceMomentsRef.current.slice(),
      });
    };
    try { rec.stop(); }
    catch { resolve({ rawBlob: null, processedBlob: null, mime: '', ext: '' }); }
  }), [recordingMime, profile]);

  // Long-session resilience.
  useEffect(() => {
    const onVisibility = () => { if (document.hidden) stop(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', stop);
    window.addEventListener('beforeunload', stop);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', stop);
      window.removeEventListener('beforeunload', stop);
      stop();
    };
  }, [stop]);

  // iter205 — devicechange listener. Bluetooth transitions are the
  // single biggest hidden risk in event-night audio: AirPods reconnect,
  // car Bluetooth, DJ speaker pairing, headphone unplug. The pipeline
  // refreshes the device list on every change so the host UI can offer
  // a "switch to the new mic ✦" affordance instead of silent failure.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) return undefined;
    const onChange = async () => {
      soakEmit('mic:devicechange', { profile: profileName });
      try {
        const list = await navigator.mediaDevices.enumerateDevices();
        setDevices(list.filter((d) => d.kind === 'audioinput'));
      } catch { /* no perm */ }
    };
    navigator.mediaDevices.addEventListener?.('devicechange', onChange);
    return () => navigator.mediaDevices.removeEventListener?.('devicechange', onChange);
  }, [profileName]);

  return {
    // refs
    rawStreamRef,
    processedStreamRef,

    // state
    status,
    error,
    deviceId,
    devices,
    recordingMime,
    profile,

    // telemetry (live + future emotional scoring)
    peakLevel,
    presenceFloor,
    clipping,
    interruptionCount: interruptionCountRef.current,
    silenceWindows: silenceWindowsRef.current,
    // iter206 — voicePresenceMoments + rawRoomToneRMS exposed for
    // live debug surfaces and downstream Event Film ✦ sequencing.
    // The stopRecording() resolution carries the final captured copy
    // — these refs reflect the live in-progress arrays.
    voicePresenceMoments: voicePresenceMomentsRef.current,
    rawRoomToneRMS: rawRoomToneRMSRef.current,

    // actions
    start,
    stop,
    switchDevice,
    startRecording,
    stopRecording,
  };
}

export default useParticipationAudioPipeline;
